// The guessing board (rows of letter tiles) and the on-screen keyboard, shared by the word
// of the day and the rooms.

import type { Lang } from "../src/languages";
import { keyboardRows, MAX_ATTEMPTS, WORD_LENGTH, type Mark } from "../src/protocol";
import type { Draft } from "./draft";
import { h } from "./dom";
import { STRINGS } from "./strings";

const MARK_CLASSES = ["tile-absent", "tile-present", "tile-correct"] as const;
const HIGH_CONTRAST_KEY = "workbrawl:guess-contrast";

/** Rows of tiles: past guesses with their colors, the word being typed, and empty rows. */
export class GuessGrid {
  readonly el = h("div", { class: "guess-grid", role: "grid", "aria-label": STRINGS.boardLabel });
  private readonly tiles: HTMLElement[][];
  /** Row being typed, or -1 when nothing can be typed. */
  private activeRow = -1;

  /** `onSelect(column)` fires when a tile of the row being typed is clicked. */
  constructor(onSelect?: (column: number) => void) {
    this.tiles = Array.from({ length: MAX_ATTEMPTS }, (_, r) => {
      const row = Array.from({ length: WORD_LENGTH }, (_, c) => {
        const tile = h("span", { class: "tile", role: "gridcell" });
        tile.addEventListener("click", () => {
          if (r === this.activeRow) onSelect?.(c);
        });
        return tile;
      });
      this.el.append(h("div", { class: "guess-row", role: "row" }, ...row));
      return row;
    });
  }

  /** `draft` is the row being typed (null when nothing can be typed); `animateLast` flips the newest row in. */
  render(guesses: string[], marks: Mark[][], draft: Draft | null, animateLast = false): void {
    this.activeRow = draft ? guesses.length : -1;
    this.tiles.forEach((row, r) => {
      const letters = r < guesses.length ? [...guesses[r]!] : r === this.activeRow ? [...draft!.letters] : [];
      row.forEach((tile, c) => {
        const mark = r < guesses.length ? marks[r]?.[c] : undefined;
        let className = "tile";
        if (mark !== undefined) className += ` ${MARK_CLASSES[mark]}`;
        else if (letters[c]) className += " tile-filled";
        if (r === this.activeRow) className += draft!.cursor === c ? " tile-active tile-cursor" : " tile-active";
        if (mark !== undefined && animateLast && r === guesses.length - 1) {
          className += " tile-reveal";
          tile.style.setProperty("--i", String(c));
        }
        tile.className = className;
        tile.textContent = letters[c]?.toUpperCase() ?? "";
      });
      const parent = row[0]!.parentElement!;
      if (r < guesses.length) {
        const result = marks[r]!.map((m) => STRINGS.markNames[m]).join(", ");
        parent.setAttribute("aria-label", STRINGS.rowLabel(r + 1, guesses[r]!.toUpperCase(), result));
      } else {
        parent.removeAttribute("aria-label");
      }
    });
  }

  /** A short shake on the row being typed, for a word that wasn't accepted. */
  shake(row: number): void {
    const el = this.tiles[row]?.[0]?.parentElement;
    if (!el) return;
    el.classList.remove("row-shake");
    void el.offsetWidth;
    el.classList.add("row-shake");
  }
}

/**
 * QWERTY keyboard with Enter and Delete, colored by what each letter has shown so far. It
 * also listens to the physical keyboard.
 */
export class Keyboard {
  readonly el = h("div", { class: "keyboard", role: "group", "aria-label": STRINGS.boardLabel });
  private keys = new Map<string, HTMLButtonElement>();
  private enabled = true;

  constructor(
    lang: Lang,
    private readonly onKey: (key: string) => void,
  ) {
    const rows = keyboardRows(lang);
    rows.forEach((letters, i) => {
      const row = h("div", { class: "keyboard-row" });
      if (i === rows.length - 1) row.append(this.special("backspace", "⌫", STRINGS.backspace));
      for (const letter of letters) {
        const key = h("button", { type: "button", class: "key" }, letter.toUpperCase());
        key.addEventListener("click", () => this.press(letter));
        this.keys.set(letter, key);
        row.append(key);
      }
      if (i === rows.length - 1) row.append(this.special("enter", STRINGS.enter, STRINGS.enter));
      this.el.append(row);
    });

    window.addEventListener("keydown", (event) => {
      // A keyboard that was replaced or left the page stops listening.
      if (!this.el.isConnected) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return;
      if (document.querySelector("dialog[open]")) return;
      if (event.key === "Enter") this.press("enter");
      else if (event.key === "Backspace") this.press("backspace");
      else if (event.key === "ArrowLeft") this.press("left");
      else if (event.key === "ArrowRight") this.press("right");
      else if ([...event.key].length === 1 && this.keys.has(event.key.toLowerCase())) this.press(event.key.toLowerCase());
      else return;
      event.preventDefault();
    });
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.el.classList.toggle("is-disabled", !enabled);
  }

  /** Best color seen for each letter across the guesses. */
  color(guesses: string[], marks: Mark[][]): void {
    const best = new Map<string, Mark>();
    guesses.forEach((guess, r) => {
      [...guess].forEach((letter, c) => {
        const mark = marks[r]![c]!;
        if ((best.get(letter) ?? -1) < mark) best.set(letter, mark);
      });
    });
    for (const [letter, key] of this.keys) {
      const mark = best.get(letter);
      key.className = mark === undefined ? "key" : `key ${MARK_CLASSES[mark]}`;
    }
  }

  private special(name: string, label: string, aria: string): HTMLButtonElement {
    const key = h("button", { type: "button", class: "key key-wide", "aria-label": aria }, label);
    key.addEventListener("click", () => this.press(name));
    return key;
  }

  private press(key: string): void {
    if (this.enabled) this.onKey(key);
  }
}

/** The colorblind-friendly palette (orange and blue), remembered per browser. */
export function contrastToggle(): HTMLElement {
  const input = h("input", { type: "checkbox", id: "contrast" });
  const apply = (on: boolean) => document.body.classList.toggle("high-contrast", on);
  let on = false;
  try {
    on = localStorage.getItem(HIGH_CONTRAST_KEY) === "1";
  } catch {
    // Storage unavailable: default colors.
  }
  input.checked = on;
  apply(on);
  input.addEventListener("change", () => {
    apply(input.checked);
    try {
      localStorage.setItem(HIGH_CONTRAST_KEY, input.checked ? "1" : "0");
    } catch {
      // See above.
    }
  });
  return h("label", { class: "contrast-toggle", for: "contrast" }, input, " ", STRINGS.highContrast);
}

/** Small rows of colored squares: someone else's progress, without their letters. */
export function miniRows(marks: Mark[][]): HTMLElement {
  return h(
    "span",
    { class: "mini-rows", "aria-hidden": "true" },
    ...marks.map((row) => h("span", { class: "mini-row" }, ...row.map((m) => h("span", { class: `mini ${MARK_CLASSES[m]}` })))),
  );
}

/** Emoji squares for sharing a result: 🟩 right place, 🟨 elsewhere, ⬜ not in the word. */
export function shareSquares(marks: Mark[][]): string {
  const squares = ["⬜", "\u{1F7E8}", "\u{1F7E9}"];
  return marks.map((row) => row.map((m) => squares[m]).join("")).join("\n");
}
